import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PageHeader } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { PRIORITIES, WO_STATUS, findMeta, eur } from "@/lib/constants";
import {
  Sparkles,
  Loader2,
  Route as RouteIcon,
  MapPin,
  X,
  CheckCircle2,
  Search,
  LocateFixed,
  Layers3,
  ExternalLink,
  Users,
  BriefcaseBusiness,
  Bell,
  Building2,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  clients: {
    color: "#0066CC",
    label: "Clientes",
    icon: Building2,
  },
  technicians: {
    color: "#34C759",
    label: "Técnicos",
    icon: Users,
  },
  work_orders: {
    color: "#FF9500",
    label: "Órdenes",
    icon: BriefcaseBusiness,
  },
  avisos: {
    color: "#FF3B30",
    label: "Avisos",
    icon: Bell,
  },
};

const ROUTE_COLORS = [
  "#0066CC",
  "#5856D6",
  "#FF9500",
  "#34C759",
  "#FF3B30",
  "#00B4D8",
];

function createPin(color, label = "") {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        display:flex;
        flex-direction:column;
        align-items:center;
        transform:translateY(-2px);
      ">
        <span style="
          display:flex;
          align-items:center;
          justify-content:center;
          width:32px;
          height:32px;
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          background:${color};
          border:3px solid white;
          box-shadow:0 5px 14px rgba(0,0,0,.28);
          color:white;
          font-size:11px;
          font-weight:700;
        ">
          <span style="transform:rotate(45deg);">${label}</span>
        </span>
      </div>
    `,
    iconSize: [32, 36],
    iconAnchor: [16, 34],
  });
}

function kmBetween(a, b) {
  const radius = 6371;
  const latitudeDifference = ((b[0] - a[0]) * Math.PI) / 180;
  const longitudeDifference = ((b[1] - a[1]) * Math.PI) / 180;

  const calculation =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) *
      Math.cos((b[0] * Math.PI) / 180) *
      Math.sin(longitudeDifference / 2) ** 2;

  return Math.round(
    2 * radius * Math.asin(Math.sqrt(calculation)) * 100
  ) / 100;
}

function itemName(item, category) {
  if (category === "work_orders" || category === "avisos") {
    return item.title || item.number || "Sin título";
  }

  return item.name || item.client_name || "Sin nombre";
}

function itemSearchText(item, category) {
  return [
    itemName(item, category),
    item.client_name,
    item.technician_name,
    item.address,
    item.sector,
    item.specialty,
    item.number,
    item.status,
    item.priority,
    item.email,
    item.phone,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#D2D2D7]/50 bg-white px-4 py-3">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ backgroundColor: color + "14" }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>

      <div>
        <p className="font-display text-xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-xs text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function MapOperativo() {
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const markerRefs = useRef({});

  const [data, setData] = useState({
    clients: [],
    technicians: [],
    work_orders: [],
    avisos: [],
  });

  const [layers, setLayers] = useState({
    clients: true,
    technicians: true,
    work_orders: true,
    avisos: true,
  });

  const [selected, setSelected] = useState(null);
  const [plan, setPlan] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showLegend, setShowLegend] = useState(true);

  const load = async () => {
    setLoading(true);

    try {
      const response = await api.get("/map/data");

      setData({
        clients: response.data?.clients || [],
        technicians: response.data?.technicians || [],
        work_orders: response.data?.work_orders || [],
        avisos: response.data?.avisos || [],
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      return;
    }

    const map = L.map("mantix-map", {
      zoomControl: true,
      attributionControl: true,
    }).setView([40.4168, -3.7038], 6);

    L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "© OpenStreetMap",
        maxZoom: 19,
      }
    ).addTo(map);

    markerLayerRef.current = L.layerGroup().addTo(map);
    routeLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    setTimeout(() => {
      map.invalidateSize();
    }, 250);

    return () => {
      map.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
      routeLayerRef.current = null;
      markerRefs.current = {};
    };
  }, []);

  const allItems = useMemo(() => {
    const result = [];

    Object.keys(CATEGORY_META).forEach((category) => {
      const categoryItems = data[category] || [];

      categoryItems.forEach((item) => {
        result.push({
          category,
          item,
          label: itemName(item, category),
          searchText: itemSearchText(item, category),
        });
      });
    });

    return result;
  }, [data]);

  const searchResults = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    if (!normalized) {
      return [];
    }

    return allItems
      .filter((entry) => entry.searchText.includes(normalized))
      .slice(0, 8);
  }, [allItems, query]);

  useEffect(() => {
    const map = mapRef.current;
    const markerLayer = markerLayerRef.current;
    const routeLayer = routeLayerRef.current;

    if (!map || !markerLayer || !routeLayer) {
      return;
    }

    markerLayer.clearLayers();
    routeLayer.clearLayers();
    markerRefs.current = {};

    const bounds = [];

    const addMarker = (item, category) => {
      if (item.lat == null || item.lng == null) {
        return;
      }

      const lat = Number(item.lat);
      const lng = Number(item.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      const label =
        category === "clients"
          ? "C"
          : category === "technicians"
            ? "T"
            : category === "work_orders"
              ? "O"
              : "A";

      const marker = L.marker([lat, lng], {
        icon: createPin(CATEGORY_META[category].color, label),
      });

      marker.on("click", () => {
        setSelected({
          category,
          item,
        });
      });

      marker.addTo(markerLayer);
      bounds.push([lat, lng]);

      markerRefs.current[category + "-" + item.id] = marker;
    };

    Object.keys(CATEGORY_META).forEach((category) => {
      if (!layers[category]) {
        return;
      }

      (data[category] || []).forEach((item) => {
        addMarker(item, category);
      });
    });

    if (plan?.plan) {
      plan.plan.forEach((route, index) => {
        const color =
          ROUTE_COLORS[index % ROUTE_COLORS.length];

        const points = [];

        if (
          route.base &&
          route.base.lat != null &&
          route.base.lng != null
        ) {
          points.push([
            Number(route.base.lat),
            Number(route.base.lng),
          ]);
        }

        (route.stops || []).forEach((stop) => {
          if (stop.lat != null && stop.lng != null) {
            points.push([
              Number(stop.lat),
              Number(stop.lng),
            ]);
          }
        });

        if (points.length > 1) {
          L.polyline(points, {
            color,
            weight: 4,
            opacity: 0.8,
            dashArray: "8 9",
            lineCap: "round",
          }).addTo(routeLayer);

          points.forEach((point) => bounds.push(point));
        }
      });
    }

    if (bounds.length > 0 && !selected) {
      try {
        map.fitBounds(bounds, {
          padding: [55, 55],
          maxZoom: 14,
        });
      } catch {
        // El mapa conserva la vista actual.
      }
    }
  }, [data, layers, plan, selected]);

  const visibleCounts = useMemo(() => {
    const result = {};

    Object.keys(CATEGORY_META).forEach((category) => {
      result[category] = (data[category] || []).filter(
        (item) => item.lat != null && item.lng != null
      ).length;
    });

    return result;
  }, [data]);

  const nearestTechnician = (item) => {
    if (
      !item ||
      item.lat == null ||
      item.lng == null
    ) {
      return null;
    }

    const available = data.technicians.filter(
      (technician) =>
        technician.lat != null &&
        technician.lng != null
    );

    if (!available.length) {
      return null;
    }

    let best = null;

    available.forEach((technician) => {
      const distance = kmBetween(
        [Number(item.lat), Number(item.lng)],
        [Number(technician.lat), Number(technician.lng)]
      );

      if (!best || distance < best.distance) {
        best = {
          technician,
          distance,
        };
      }
    });

    return best;
  };

  const focusItem = (entry) => {
    const { category, item } = entry;

    setSelected({
      category,
      item,
    });

    setQuery("");

    if (
      item.lat == null ||
      item.lng == null ||
      !mapRef.current
    ) {
      toast.info("Este elemento no tiene coordenadas.");
      return;
    }

    const marker =
      markerRefs.current[category + "-" + item.id];

    mapRef.current.setView(
      [Number(item.lat), Number(item.lng)],
      16,
      {
        animate: true,
      }
    );

    if (marker) {
      marker.openPopup();
    }
  };

  const fitAll = () => {
    const map = mapRef.current;

    if (!map) {
      return;
    }

    const points = [];

    Object.keys(CATEGORY_META).forEach((category) => {
      if (!layers[category]) {
        return;
      }

      (data[category] || []).forEach((item) => {
        if (item.lat != null && item.lng != null) {
          points.push([
            Number(item.lat),
            Number(item.lng),
          ]);
        }
      });
    });

    if (!points.length) {
      toast.info("No hay ubicaciones visibles.");
      return;
    }

    map.fitBounds(points, {
      padding: [60, 60],
      maxZoom: 14,
    });
  };

  const assign = async (
    item,
    category,
    technicianId
  ) => {
    try {
      if (category === "work_orders") {
        await api.put(
          "/work-orders/" + item.id,
          {
            technician_id:
              technicianId || null,
          }
        );
      } else {
        await api.put(
          "/avisos/" + item.id,
          {
            assigned_technician_id:
              technicianId || null,
            status: technicianId
              ? "asignado"
              : "nuevo",
          }
        );
      }

      toast.success("Asignación actualizada.");
      setSelected(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const optimize = async () => {
    setOptimizing(true);
    setSelected(null);

    try {
      const response = await api.post(
        "/ai/optimize-day",
        {}
      );

      setPlan(response.data);

      if (!response.data?.plan?.length) {
        toast.info(
          response.data?.ai_summary ||
            "No hay trabajos para optimizar."
        );
      } else {
        toast.success(
          "Jornada optimizada."
        );
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setOptimizing(false);
    }
  };

  const applyPlan = async () => {
    if (!plan?.plan) {
      return;
    }

    setApplying(true);

    try {
      const tasks = [];

      plan.plan.forEach((route) => {
        (route.stops || []).forEach(
          (stop) => {
            if (stop.auto_assigned) {
              tasks.push(
                api.put(
                  "/work-orders/" +
                    stop.wo_id,
                  {
                    technician_id:
                      route.technician_id,
                  }
                )
              );
            }
          }
        );
      });

      await Promise.all(tasks);

      toast.success(
        tasks.length
          ? tasks.length +
              " asignaciones aplicadas."
          : "No había asignaciones nuevas."
      );

      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setApplying(false);
    }
  };

  const openExternalMap = (item) => {
    if (
      item.lat == null ||
      item.lng == null
    ) {
      toast.info("Este elemento no tiene coordenadas.");
      return;
    }

    const url =
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(
        String(item.lat) +
          "," +
          String(item.lng)
      );

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const selectedItem = selected?.item;
  const near = selectedItem
    ? nearestTechnician(selectedItem)
    : null;

  const selectedMeta = selected
    ? CATEGORY_META[selected.category]
    : null;

  return (
    <div className="flex h-screen flex-col p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Mapa Operativo"
        subtitle="Clientes, técnicos, avisos y órdenes en una sola vista"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={fitAll}
              className="flex items-center gap-2 rounded-full border border-[#D2D2D7] bg-white px-4 py-2.5 text-sm font-medium text-[#1D1D1F] hover:bg-[#F5F5F7]"
            >
              <LocateFixed className="h-4 w-4" />
              Ver todo
            </button>

            <button
              data-testid="optimize-day-btn"
              type="button"
              onClick={optimize}
              disabled={optimizing}
              className="flex items-center gap-2 rounded-full bg-gradient-to-br from-[#0066CC] to-[#5856D6] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
              {optimizing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Optimizar jornada
            </button>
          </div>
        }
      />

      <section className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Object.entries(CATEGORY_META).map(
          ([category, meta]) => (
            <SummaryCard
              key={category}
              icon={meta.icon}
              label={meta.label}
              value={visibleCounts[category] || 0}
              color={meta.color}
            />
          )
        )}
      </section>

      <div className="flex min-h-0 flex-1 gap-5">
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-3xl border border-[#D2D2D7]/50 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div
            id="mantix-map"
            data-testid="operations-map"
            className="h-full w-full"
          />

          {loading && (
            <div className="absolute inset-0 z-[700] flex items-center justify-center bg-white/70 backdrop-blur-sm">
              <div className="rounded-2xl bg-white px-6 py-5 text-center shadow-lg">
                <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />
                <p className="mt-3 text-sm text-[#86868B]">
                  Cargando mapa...
                </p>
              </div>
            </div>
          )}

          <div className="absolute left-4 top-4 z-[600] w-[min(360px,calc(100%-32px))]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

              <input
                value={query}
                onChange={(event) =>
                  setQuery(event.target.value)
                }
                placeholder="Buscar cliente, técnico, aviso u orden..."
                className="w-full rounded-2xl border border-[#D2D2D7]/60 bg-white/95 py-3 pl-11 pr-10 text-sm shadow-lg outline-none backdrop-blur focus:ring-2 focus:ring-[#0066CC]/20"
              />

              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] hover:bg-[#F5F5F7]"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {query && (
              <div className="mt-2 max-h-80 overflow-y-auto rounded-2xl border border-[#D2D2D7]/60 bg-white p-2 shadow-xl">
                {searchResults.length ? (
                  searchResults.map((entry) => {
                    const meta =
                      CATEGORY_META[
                        entry.category
                      ];

                    return (
                      <button
                        key={
                          entry.category +
                          "-" +
                          entry.item.id
                        }
                        type="button"
                        onClick={() =>
                          focusItem(entry)
                        }
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[#F5F5F7]"
                      >
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{
                            backgroundColor:
                              meta.color,
                          }}
                        />

                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#1D1D1F]">
                            {entry.label}
                          </p>
                          <p className="text-xs text-[#86868B]">
                            {meta.label}
                          </p>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="px-3 py-4 text-center text-sm text-[#86868B]">
                    No hay resultados.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="absolute right-4 top-4 z-[600]">
            <button
              type="button"
              onClick={() =>
                setShowLegend(
                  (current) => !current
                )
              }
              className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/95 text-[#1D1D1F] shadow-lg backdrop-blur hover:bg-white"
              aria-label="Mostrar filtros"
            >
              <Layers3 className="h-5 w-5" />
            </button>

            {showLegend && (
              <div className="mt-2 min-w-48 rounded-2xl bg-white/95 p-3 shadow-lg backdrop-blur">
                {Object.entries(
                  CATEGORY_META
                ).map(
                  ([category, meta]) => (
                    <label
                      key={category}
                      data-testid={
                        "layer-" + category
                      }
                      className="flex cursor-pointer items-center gap-2 rounded-lg py-1.5 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={
                          layers[category]
                        }
                        onChange={(
                          event
                        ) =>
                          setLayers({
                            ...layers,
                            [category]:
                              event.target
                                .checked,
                          })
                        }
                        className="accent-[#0066CC]"
                      />

                      <span
                        className="h-3 w-3 rounded-full"
                        style={{
                          backgroundColor:
                            meta.color,
                        }}
                      />

                      <span className="flex-1">
                        {meta.label}
                      </span>

                      <span className="text-xs text-[#86868B]">
                        {visibleCounts[
                          category
                        ] || 0}
                      </span>
                    </label>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        <aside className="hidden w-96 shrink-0 flex-col overflow-y-auto rounded-3xl border border-[#D2D2D7]/50 bg-white p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)] xl:flex">
          {selected ? (
            <div data-testid="map-selection">
              <div className="mb-4 flex items-start justify-between">
                <span
                  className="rounded-full px-3 py-1 text-xs font-medium text-white"
                  style={{
                    backgroundColor:
                      selectedMeta.color,
                  }}
                >
                  {selectedMeta.label}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setSelected(null)
                  }
                  className="rounded-lg p-1 text-[#86868B] hover:bg-[#F5F5F7] hover:text-[#1D1D1F]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <h3 className="font-display text-xl font-semibold text-[#1D1D1F]">
                {selectedItem.title ||
                  selectedItem.name ||
                  "Sin título"}
              </h3>

              {selectedItem.number && (
                <p className="mt-1 font-mono text-xs text-[#86868B]">
                  {selectedItem.number}
                </p>
              )}

              {selectedItem.client_name && (
                <p className="mt-2 text-sm text-[#86868B]">
                  {
                    selectedItem.client_name
                  }
                </p>
              )}

              {selectedItem.address && (
                <p className="mt-3 flex items-start gap-2 text-sm text-[#86868B]">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                  {selectedItem.address}
                </p>
              )}

              {selectedItem.sector && (
                <p className="mt-2 text-sm text-[#86868B]">
                  Sector:{" "}
                  {selectedItem.sector}
                </p>
              )}

              {selectedItem.specialty && (
                <p className="mt-2 text-sm text-[#86868B]">
                  Especialidad:{" "}
                  {
                    selectedItem.specialty
                  }
                </p>
              )}

              {selectedItem.priority && (
                <div className="mt-3">
                  <span
                    className="rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      color: findMeta(
                        PRIORITIES,
                        selectedItem.priority
                      ).color,
                      backgroundColor:
                        findMeta(
                          PRIORITIES,
                          selectedItem.priority
                        ).bg,
                    }}
                  >
                    {
                      findMeta(
                        PRIORITIES,
                        selectedItem.priority
                      ).label
                    }
                  </span>
                </div>
              )}

              {selectedItem.status &&
                selected.category ===
                  "work_orders" && (
                  <div className="mt-3">
                    <span
                      className="rounded-full px-3 py-1 text-xs font-medium"
                      style={{
                        color: findMeta(
                          WO_STATUS,
                          selectedItem.status
                        ).color,
                        backgroundColor:
                          findMeta(
                            WO_STATUS,
                            selectedItem.status
                          ).bg,
                      }}
                    >
                      {
                        findMeta(
                          WO_STATUS,
                          selectedItem.status
                        ).label
                      }
                    </span>
                  </div>
                )}

              {selectedItem.amount != null &&
                selectedItem.title && (
                  <p className="mt-4 font-mono text-lg font-semibold text-[#1D1D1F]">
                    {eur(
                      selectedItem.amount
                    )}
                  </p>
                )}

              {near && (
                <div
                  className="mt-5 rounded-2xl bg-[#E9F9EE] p-4 text-sm text-[#1D8A3F]"
                  data-testid="nearest-tech"
                >
                  <p className="flex items-center gap-2 font-medium">
                    <MapPin className="h-4 w-4" />
                    Técnico más cercano
                  </p>
                  <p className="mt-1">
                    {
                      near.technician.name
                    }{" "}
                    · {near.distance} km
                  </p>
                </div>
              )}

              {(selected.category ===
                "work_orders" ||
                selected.category ===
                  "avisos") && (
                <div className="mt-5">
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Asignar técnico
                  </label>

                  <select
                    data-testid="map-assign-select"
                    defaultValue={
                      selectedItem.technician_id ||
                      selectedItem.assigned_technician_id ||
                      ""
                    }
                    onChange={(event) =>
                      assign(
                        selectedItem,
                        selected.category,
                        event.target.value
                      )
                    }
                    className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0066CC]/20"
                  >
                    <option value="">
                      Sin asignar
                    </option>

                    {data.technicians.map(
                      (technician) => (
                        <option
                          key={
                            technician.id
                          }
                          value={
                            technician.id
                          }
                        >
                          {
                            technician.name
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              )}

              <button
                type="button"
                onClick={() =>
                  openExternalMap(
                    selectedItem
                  )
                }
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-[#D2D2D7] bg-white py-2.5 text-sm font-medium text-[#0066CC] hover:bg-[#F5F5F7]"
              >
                <ExternalLink className="h-4 w-4" />
                Abrir en Google Maps
              </button>
            </div>
          ) : plan ? (
            <div data-testid="optimization-plan">
              <div className="mb-4 flex items-center gap-2">
                <RouteIcon className="h-5 w-5 text-[#0066CC]" />
                <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                  Jornada optimizada
                </h3>
              </div>

              {!plan.plan?.length ? (
                <p className="text-sm text-[#86868B]">
                  {
                    plan.ai_summary
                  }
                </p>
              ) : (
                <>
                  {plan.plan.map(
                    (route, index) => (
                      <div
                        key={
                          route.technician_id
                        }
                        className="mb-4 rounded-2xl border border-[#D2D2D7]/50 p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex min-w-0 items-center gap-2 font-medium text-[#1D1D1F]">
                            <span
                              className="h-3 w-3 shrink-0 rounded-full"
                              style={{
                                backgroundColor:
                                  ROUTE_COLORS[
                                    index %
                                      ROUTE_COLORS.length
                                  ],
                              }}
                            />
                            <span className="truncate">
                              {
                                route.technician_name
                              }
                            </span>
                          </span>

                          <span className="shrink-0 font-mono text-sm text-[#0066CC]">
                            {route.total_km} km
                          </span>
                        </div>

                        <ol className="mt-3 space-y-2 text-sm text-[#86868B]">
                          {(route.stops || []).map(
                            (stop) => (
                              <li
                                key={
                                  stop.wo_id
                                }
                                className="flex items-center gap-2"
                              >
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F5F5F7] text-xs font-semibold text-[#1D1D1F]">
                                  {
                                    stop.seq
                                  }
                                </span>

                                <span className="min-w-0 flex-1 truncate">
                                  {
                                    stop.client_name
                                  }
                                </span>

                                <span className="shrink-0 text-xs">
                                  {stop.leg_km} km
                                </span>
                              </li>
                            )
                          )}
                        </ol>
                      </div>
                    )
                  )}

                  <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#0066CC]/5 to-[#5856D6]/5 p-3">
                    <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[#0066CC]">
                      <Sparkles className="h-3.5 w-3.5" />
                      Análisis
                    </p>

                    <p className="whitespace-pre-wrap text-sm text-[#1D1D1F]">
                      {
                        plan.ai_summary
                      }
                    </p>
                  </div>

                  <button
                    data-testid="apply-plan-btn"
                    type="button"
                    onClick={applyPlan}
                    disabled={applying}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] py-2.5 font-medium text-white hover:bg-[#004499] disabled:opacity-60"
                  >
                    {applying ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    Aplicar asignaciones
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-[#86868B]">
              <MapPin
                className="mb-3 h-10 w-10 text-[#D2D2D7]"
                strokeWidth={1.5}
              />

              <p className="text-sm leading-relaxed">
                Selecciona un punto del mapa para ver sus detalles, asignar técnicos o abrir la ubicación.
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}